/* eslint-disable react/jsx-props-no-spreading */
import { faUpload } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { EsriLocation } from '../../../../api/ach/esri';
import { parseLocationCsv } from '../../../../util/esriLocationCsv';

const MAX_SHOWN_ERRORS = 5;

const columns: GridColDef[] = [
  { field: 'name', headerName: 'Name', flex: 1, minWidth: 120 },
  { field: 'latitude', headerName: 'Latitude', width: 120, type: 'number' },
  { field: 'longitude', headerName: 'Longitude', width: 120, type: 'number' },
  { field: 'radius', headerName: 'Radius (m)', width: 120, type: 'number' },
];

const readText = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });

interface Props {
  locations: EsriLocation[];
  onChange: (locations: EsriLocation[]) => void;
  disabled?: boolean;
}

function EsriLocationUpload({ locations, onChange, disabled }: Props) {
  const [fileName, setFileName] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  const load = async ([file]: File[]) => {
    if (!file) return;
    setFileName(file.name);
    try {
      const parsed = parseLocationCsv(await readText(file));
      setErrors(parsed.errors);
      onChange(parsed.locations);
    } catch {
      setErrors(['Could not read the file.']);
      onChange([]);
    }
  };

  const clear = () => {
    setFileName(null);
    setErrors([]);
    onChange([]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'text/csv': ['.csv', '.tsv', '.txt'] },
    multiple: false,
    disabled,
    onDropAccepted: load,
    onDropRejected: () => setErrors(['Please upload a .csv, .tsv or .txt file.']),
  });

  return (
    <Stack spacing={1}>
      {locations.length === 0 ? (
        <Paper
          variant="outlined"
          {...getRootProps()}
          sx={{
            p: 3,
            textAlign: 'center',
            cursor: disabled ? 'default' : 'pointer',
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'divider',
            bgcolor: isDragActive ? 'action.hover' : 'transparent',
          }}
        >
          <input {...getInputProps()} aria-label="Location file" />
          <FontAwesomeIcon icon={faUpload} color="rgb(153, 153, 153)" />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Drop a CSV here or click to browse
          </Typography>
        </Paper>
      ) : (
        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {fileName}: {locations.length.toLocaleString()} location{locations.length === 1 ? '' : 's'} loaded
            </Typography>
            <Button size="small" onClick={clear} disabled={disabled}>
              Clear
            </Button>
          </Stack>
          <DataGrid
            rows={locations.map((location, id) => ({ id, ...location }))}
            columns={columns}
            density="compact"
            pageSizeOptions={[5, 10]}
            initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
            disableRowSelectionOnClick
            autoHeight
          />
        </Box>
      )}
      {errors.slice(0, MAX_SHOWN_ERRORS).map((error) => (
        <Alert key={error} severity="warning">
          {error}
        </Alert>
      ))}
      {errors.length > MAX_SHOWN_ERRORS && (
        <Typography variant="caption" color="text.secondary">
          …and {(errors.length - MAX_SHOWN_ERRORS).toLocaleString()} more warnings
        </Typography>
      )}
    </Stack>
  );
}

export default EsriLocationUpload;
